<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

defineProps({
  collapsed: Boolean
});

const categories = [
  { id: 'home', name: 'Home', icon: 'house-door-fill' },
  { id: 'Arcade', name: 'Arcade', icon: 'people-fill' },
  { id: 'Puzzle', name: 'Puzzle', icon: 'compass-fill' },
  { id: 'Strategy', name: 'Strategy', icon: 'flower1' },
  { id: 'Sports', name: 'Sports', icon: 'car-front-fill' },
  { id: 'originals', name: 'originals', icon: 'controller' },
  { id: 'Quiz', name: 'Quiz', icon: 'dpad-fill' },
  { id: 'action', name: 'Action', icon: 'lightning-fill' },
  { id: 'basketball', name: 'Basketball', icon: 'dribbble' },
  { id: 'bike', name: 'Bike', icon: 'bicycle' },
  { id: 'card', name: 'Card', icon: 'suit-club-fill' },
  { id: 'clicker', name: 'Clicker', icon: 'mouse-fill' },
  { id: 'dress-up', name: 'Dress Up', icon: 'stars' }
];

const isExpanded = ref(false);
const isMobile = ref(window.innerWidth <= 480);
const menuClicked = ref(false);

const handleResize = () => {
  isMobile.value = window.innerWidth <= 480;
  if (!isMobile.value) {
    menuClicked.value = false;
  }
};

const toggleExpand = (e) => {
  if (isMobile.value) {
    if (!menuClicked.value) {
      menuClicked.value = true;
      isExpanded.value = true;
      e.preventDefault();
    }
  }
};

const handleMouseEnter = () => {
  if (!isMobile.value) {
    isExpanded.value = true;
  }
};

const handleMouseLeave = () => {
  if (!isMobile.value) {
    isExpanded.value = false;
    menuClicked.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <aside 
    class="sidebar" 
    :class="{ 'collapsed': collapsed, 'expanded': isExpanded && !collapsed }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="sidebar-content">
      <div class="py-2">
        <router-link 
          v-for="category in categories" 
          :key="category.id"
          :to="category.id === 'home' ? '/' : `/category/${category.id}`" 
          class="sidebar-link"
          :class="{ 'active': $route.path === (category.id === 'home' ? '/' : `/category/${category.id}`) }"
          :title="category.name"
          @click="toggleExpand"
        >
          <i :class="`bi bi-${category.icon}`"></i>
          <span class="sidebar-text">{{ category.name }}</span>
        </router-link>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 70px;
  background-color: #1a1b26;
  height: calc(100vh - 72px);
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1030;
  position: fixed;
  top: 90px;
  left: 0;
  bottom: 0;
}

.sidebar.expanded {
  width: 250px;
}

.sidebar.collapsed {
  width: 0;
  padding: 0;
  overflow: hidden;
  transform: translateX(-100%);
}

.sidebar-content {
  height: 100%;
  overflow-y: auto;
}

.sidebar-link {
  color: #e5e7eb;
  text-decoration: none;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
}

.sidebar-link:hover,
.sidebar-link.active {
  background-color: rgba(126, 58, 242, 0.15);
  color: #7e3af2;
}

.sidebar-link i {
  font-size: 1.25rem;
  min-width: 24px;
  text-align: center;
}

.sidebar-text {
  margin-left: 1rem;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.expanded .sidebar-text {
  opacity: 1;
  visibility: visible;
}

/* Custom scrollbar for sidebar */
.sidebar::-webkit-scrollbar {
  width: 4px;
}

.sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar::-webkit-scrollbar-thumb {
  background: rgba(126, 58, 242, 0.5);
  border-radius: 4px;
}

@media (max-width: 768px) {
  .sidebar {
    top: 80px;
    height: calc(100vh - 60px);
  }

  .sidebar-content {
    padding-top: 0;
  }

  .sidebar.collapsed {
    transform: translateX(-100%);
  }
}

@media (max-width: 480px) {
  .sidebar {
    width: 80px;
  }

  .sidebar.expanded {
    width: 200px;
  }

  .sidebar-link {
    padding: 0.75rem 1rem;
  }
}
</style>